import moment from "moment";
import { useMemo, useState } from "react";
import { debounce } from "ts-debounce";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import {
  Space,
  Button,
  Table,
  Tag,
  Select,
  Popconfirm,
  Modal,
  Spin,
  message,
} from "antd";

import sdk from "@/sdk";
import { UnmatchedDepositStatus, UnmatchedDepositReason } from "@/graphql";
import { formatBalance } from "@/utils/formatters";

// Keyed by string (not the generated enum) so the codegen lag between the
// backend schema and the dashboard's generated types does not break the build
// when a new reason (e.g. ambiguous) is added.
const reasonLabels: Record<string, string> = {
  [UnmatchedDepositReason.NoRegexMatch]: "Тайлбар таарахгүй",
  [UnmatchedDepositReason.UserNotFound]: "Хэрэглэгч олдсонгүй",
  [UnmatchedDepositReason.AmountTooLow]: "Дүн бага",
  ambiguous: "Олон тохирол",
};

const reasonColors: Record<string, string> = {
  [UnmatchedDepositReason.NoRegexMatch]: "red",
  [UnmatchedDepositReason.UserNotFound]: "orange",
  [UnmatchedDepositReason.AmountTooLow]: "default",
  ambiguous: "gold",
};

const statusLabels: Record<UnmatchedDepositStatus, string> = {
  [UnmatchedDepositStatus.Pending]: "Хүлээгдэж буй",
  [UnmatchedDepositStatus.Matched]: "Холбогдсон",
  [UnmatchedDepositStatus.Dismissed]: "Цуцлагдсан",
};

const statusColors: Record<UnmatchedDepositStatus, string> = {
  [UnmatchedDepositStatus.Pending]: "processing",
  [UnmatchedDepositStatus.Matched]: "success",
  [UnmatchedDepositStatus.Dismissed]: "default",
};

const bankLabels: Record<string, string> = {
  "khaan-bank": "Хаан банк",
  "golomt-bank": "Голомт банк",
};

export const UnmatchedDepositsWidget = () => {
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [statusFilter, setStatusFilter] =
    useState<UnmatchedDepositStatus | null>(UnmatchedDepositStatus.Pending);
  const [bankFilter, setBankFilter] = useState<string | null>(null);

  const [matchModalOpen, setMatchModalOpen] = useState(false);
  const [matchingId, setMatchingId] = useState<string | null>(null);
  const [matchUserId, setMatchUserId] = useState("");
  const [userSearch, setUserSearch] = useState("");

  // Search users (phone / email / name / register number / deposit id) the same
  // way the users page does, so an admin can pick the recipient instead of
  // pasting a raw MongoDB id.
  const { data: userResults, isFetching: isSearchingUsers } = useQuery({
    queryKey: ["unmatchedDeposits", "userSearch", userSearch],
    queryFn: () =>
      sdk.getUsers({
        page: 0,
        limit: 20,
        advanced: { search: userSearch || undefined },
      }),
    select: (data) => data.getUsers.users,
    enabled: matchModalOpen,
  });
  const debouncedSetUserSearch = useMemo(
    () => debounce((value: string) => setUserSearch(value), 400),
    [],
  );

  const queryKey = ["unmatchedDeposits", page, statusFilter, bankFilter];
  const queryClient = useQueryClient();

  const { isLoading, data } = useQuery({
    queryKey,
    queryFn: () =>
      sdk.getUnmatchedDeposits({
        status: statusFilter ?? undefined,
        bank: bankFilter ?? undefined,
        skip: page * limit,
        limit,
      }),
    select: (data) => data.unmatchedDeposits,
  });

  const matchMutation = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      sdk.matchUnmatchedDeposit({ id, userId }),
    onSuccess: () => {
      message.success("Гүйлгээ амжилттай холбогдлоо.");
      queryClient.invalidateQueries({ queryKey: ["unmatchedDeposits"] });
      setMatchModalOpen(false);
      setMatchingId(null);
      setMatchUserId("");
    },
    onError: (err: any) =>
      message.error(err.response?.errors?.[0]?.message ?? "Алдаа гарлаа."),
  });

  const dismissMutation = useMutation({
    mutationFn: (id: string) => sdk.dismissUnmatchedDeposit({ id }),
    onSuccess: () => {
      message.success("Гүйлгээ цуцлагдлаа.");
      queryClient.invalidateQueries({ queryKey: ["unmatchedDeposits"] });
    },
    onError: (err: any) =>
      message.error(err.response?.errors?.[0]?.message ?? "Алдаа гарлаа."),
  });

  const openMatchModal = (id: string) => {
    setMatchingId(id);
    setMatchUserId("");
    setUserSearch("");
    setMatchModalOpen(true);
  };

  return (
    <>
      <Modal
        title="Хэрэглэгчтэй холбох"
        open={matchModalOpen}
        onCancel={() => setMatchModalOpen(false)}
        onOk={() => {
          const OBJECT_ID_REGEX = /^[a-f\d]{24}$/i;
          if (!matchingId || !OBJECT_ID_REGEX.test(matchUserId.trim())) {
            message.warning("24 тэмдэгт бүхий MongoDB ObjectId оруулна уу.");
            return;
          }
          matchMutation.mutate({ id: matchingId, userId: matchUserId.trim() });
        }}
        confirmLoading={matchMutation.isLoading}
        okText="Холбох"
        cancelText="Цуцлах"
        okButtonProps={{ disabled: !matchUserId }}
      >
        <p>Хэрэглэгчийг хайж сонгоно уу:</p>
        <Select
          showSearch
          allowClear
          value={matchUserId || undefined}
          style={{ width: "100%" }}
          placeholder="Утас, имэйл, нэр, эсвэл регистрээр хайх"
          filterOption={false}
          loading={isSearchingUsers}
          notFoundContent={isSearchingUsers ? <Spin size="small" /> : null}
          onSearch={(value) => debouncedSetUserSearch(value)}
          onChange={(value) => setMatchUserId(value ?? "")}
        >
          {userResults?.map((user) => (
            <Select.Option key={user._id} value={user._id}>
              {[user.lastName, user.firstName].filter(Boolean).join(" ") || "—"}
              {" · "}
              {user.phoneNo ?? user.email ?? user._id}
            </Select.Option>
          ))}
        </Select>
      </Modal>

      <div className="page-header">
        <h3>Танигдаагүй гүйлгээнүүд</h3>
        <Space style={{ marginTop: 8 }}>
          <Select
            allowClear
            placeholder="Төлөв"
            value={statusFilter}
            onChange={(val) => {
              setStatusFilter(val ?? null);
              setPage(0);
            }}
            style={{ width: 160 }}
          >
            {Object.values(UnmatchedDepositStatus).map((s) => (
              <Select.Option key={s} value={s}>
                {statusLabels[s]}
              </Select.Option>
            ))}
          </Select>
          <Select
            allowClear
            placeholder="Банк"
            value={bankFilter}
            onChange={(val) => {
              setBankFilter(val ?? null);
              setPage(0);
            }}
            style={{ width: 160 }}
          >
            {Object.entries(bankLabels).map(([value, label]) => (
              <Select.Option key={value} value={value}>
                {label}
              </Select.Option>
            ))}
          </Select>
        </Space>
      </div>
      <div className="page-body">
        <div className="page-table">
          <Table
            scroll={{ x: 1200 }}
            dataSource={data?.items ?? []}
            rowKey="_id"
            size="small"
            loading={isLoading}
            pagination={{
              current: page + 1,
              pageSize: limit,
              total: data?.total ?? 0,
              showTotal: (total) => `Нийт: ${total}`,
            }}
            onChange={(pagination) => setPage(Number(pagination?.current) - 1)}
            bordered
            columns={[
              {
                title: "Банк",
                dataIndex: "bank",
                key: "bank",
                width: 110,
                render: (text: string) => bankLabels[text] ?? text,
              },
              {
                title: "Дүн",
                dataIndex: "amount",
                key: "amount",
                width: 130,
                render: (text: number) => (
                  <span>{formatBalance({ balance: text })} ₮</span>
                ),
              },
              {
                title: "Гүйлгээний тайлбар",
                dataIndex: "transactionDescription",
                key: "transactionDescription",
                ellipsis: true,
              },
              {
                title: "Шалтгаан",
                dataIndex: "reason",
                key: "reason",
                width: 170,
                render: (reason: UnmatchedDepositReason) => (
                  <Tag color={reasonColors[reason] ?? "default"}>
                    {reasonLabels[reason] ?? reason}
                  </Tag>
                ),
              },
              {
                title: "External ID",
                dataIndex: "parsedExternalId",
                key: "parsedExternalId",
                width: 150,
                render: (text: string | null) => text ?? "-",
              },
              {
                title: "Төлөв",
                dataIndex: "status",
                key: "status",
                width: 130,
                render: (status: UnmatchedDepositStatus) => (
                  <Tag color={statusColors[status]}>{statusLabels[status]}</Tag>
                ),
              },
              {
                title: "Огноо",
                dataIndex: "createdAt",
                key: "createdAt",
                width: 150,
                render: (text: string) =>
                  moment(text).format("YYYY/MM/DD HH:mm"),
              },
              {
                title: "Үйлдэл",
                key: "actions",
                width: 100,
                fixed: "right" as const,
                render: (
                  _: unknown,
                  record: { _id: string; status: UnmatchedDepositStatus },
                ) => {
                  if (record.status !== UnmatchedDepositStatus.Pending) {
                    return null;
                  }
                  return (
                    <Space>
                      <Button
                        type="primary"
                        size="small"
                        icon={<CheckOutlined />}
                        onClick={() => openMatchModal(record._id)}
                        title="Хэрэглэгчтэй холбох"
                      />
                      <Popconfirm
                        title="Цуцлахдаа итгэлтэй байна уу?"
                        okText="Тийм"
                        cancelText="Үгүй"
                        okButtonProps={{ loading: dismissMutation.isLoading }}
                        onConfirm={() => dismissMutation.mutate(record._id)}
                      >
                        <Button
                          danger
                          size="small"
                          icon={<CloseOutlined />}
                          title="Цуцлах"
                        />
                      </Popconfirm>
                    </Space>
                  );
                },
              },
            ]}
          />
        </div>
      </div>
    </>
  );
};
